import jwtDecode from 'jwt-decode';

import {
  AUTO_LOGIN_BROKER_COMPANY_HOME_PATH,
  AUTO_LOGIN_HOME_PATH,
  AUTO_LOGIN_HOME_PATH_BY_SOURCE_MODE,
  PROMPT_PASSKEY_ENROLL_QUERY_PARAM,
  REFRESH_TOKEN_EXPIRES_AT_QUERY_PARAM,
  REFRESH_TOKEN_QUERY_PARAM,
  SOURCE_MODE_JWT_CLAIM_TYPE,
  TOKEN_QUERY_PARAM,
} from './AutoLogin.constants';

/* Parses the auto-login handoff tokens out of a URL query string. The access
   token is a JWT; the refresh token is opaque so its expiry rides alongside it.
   Returns null when no access token is present (i.e. not an auto-login handoff). */
export const parseAutoLoginTokensFromSearch = search => {
  const searchParams = new URLSearchParams(search);
  const token = searchParams.get(TOKEN_QUERY_PARAM);

  if (!token) {
    return null;
  }

  return {
    token,
    refreshToken: searchParams.get(REFRESH_TOKEN_QUERY_PARAM),
    refreshTokenExpiresAt: searchParams.get(REFRESH_TOKEN_EXPIRES_AT_QUERY_PARAM),
  };
};

/* DEV-4018: whether the auto-login handoff asked us to arm the one-time passkey enrollment prompt.
   Set by the TPO signup handoff for a PasskeyWithUsername company so the broker — who reaches AdminPanel
   via this handoff, not the Login page — still gets the enroll-on-first-login prompt (DEV-3881). */
export const shouldPromptPasskeyEnrollFromSearch = search =>
  new URLSearchParams(search).get(PROMPT_PASSKEY_ENROLL_QUERY_PARAM) === 'true';

/* Reads the SourceMode claim from a (possibly invalid) auto-login JWT. Returns
   null when the token is malformed or the claim is absent so the caller falls
   back to the default landing path. */
export const extractSourceModeFromToken = token => {
  try {
    const decodedToken = jwtDecode(token);

    return decodedToken?.[SOURCE_MODE_JWT_CLAIM_TYPE] ?? null;
  } catch {
    return null;
  }
};

/* Resolves where to land an auto-logged-in user. A source-mode handoff (e.g.
   TPO_CERT) always wins; otherwise a broker-company user defaults to the Company
   Center, and everyone else to the default home. */
export const resolveAutoLoginHomePath = ({ sourceMode, isBrokerCompany }) => {
  const sourceModeHomePath = AUTO_LOGIN_HOME_PATH_BY_SOURCE_MODE[sourceMode];

  if (sourceModeHomePath) {
    return sourceModeHomePath;
  }

  if (isBrokerCompany) {
    return AUTO_LOGIN_BROKER_COMPANY_HOME_PATH;
  }

  return AUTO_LOGIN_HOME_PATH;
};
